import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { ProvidersListDialogComponent } from 'app/library/certification-provider/components/dialogs/providers-list-dialog/providers-list-dialog.component';
import { CertificationProviderStatusEnum } from 'app/library/certification-provider/enums/certification-provider-status.enum';
import { CertificationProviderTypeEnum } from 'app/library/certification-provider/enums/certification-provider-type.enum';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { CertificationProviderStatus } from 'app/library/certification-provider/models/certification-provider-status.model';
import {
    CreateCertification,
    CreateCertificationFailure,
    CreateCertificationSuccess,
    CreateInternalCertification,
    CreateInternalCertificationFailure,
    CreateInternalCertificationSuccess,
    MoveCertification,
    MoveCertificationSuccess,
    SearchCertifications,
    UpdateCertification,
    UpdateCertificationFailure,
    UpdateCertificationSuccess,
} from 'app/library/certifications/actions';
import { Certification } from 'app/library/certifications/models/certification';
import { CertificationApiService } from 'app/library/certifications/services/certification.api-service';
import { CertificationService } from 'app/library/certifications/services/certification.service';
import { ModuleCertificateApiService } from 'app/library/module-certificate/services/module-certificate.api-service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { NameToggleDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/name-toggle/name-toggle.component';
import { HelperService } from 'app/projects/shared/src/lib/services/helper';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { CertificationsDataSource } from '../../datasource';
import { InternalCertificationTemplateDialogComponent } from '../internal-certification-template-dialog/internal-certification-template-dialog.component';

@Component({
    selector: 'app-certifications-dialog',
    templateUrl: './certifications-dialog.component.html',
    styleUrls: ['./certifications-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CertificationsDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    entitie$: Observable<Certification[]>;

    @Input()
    selectedProvider: CertificationProvider;

    @Input()
    filteredCertificationProviders$: Observable<CertificationProvider[]>;

    @Input()
    certificationProvidersTotalCount$: Observable<number>;

    dataSource: CertificationsDataSource;
    displayedColumns = ['name', 'status', 'targetCount', 'buttons'];
    certificationProviderStatus = CertificationProviderStatus;
    certificationProviderStatuses = CertificationProviderStatus.all;
    searchInput = new FormControl();
    statusInput = new FormControl([CertificationProviderStatusEnum.Active]);
    isFilterOrSearchActive: boolean = false;
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    certificateKeywords: string[] = [];
    CertificationProviderStatusEnum = CertificationProviderStatusEnum;
    saveBtnLoading$ = new BehaviorSubject<boolean>(false);

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<CertificationsDialogComponent>,
        private _action$: Actions,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _certificationService: CertificationService,
        private _moduleCertificateApiService: ModuleCertificateApiService,
        private _helperService: HelperService,
        private _certificationApiService: CertificationApiService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this._store.dispatch(new SearchCertifications(searchText));
            this.loadCertificationsPage(true);
        });

        this.statusInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadCertificationsPage(true);
        });

        this.dataSource = new CertificationsDataSource(this.entitie$, this._store, this._action$);

        this.loadCertificationsPage();

        if (this.selectedProvider.type === CertificationProviderTypeEnum.Internal) {
            this.displayedColumns.splice(1, 0, 'validityPeriod');
            this._moduleCertificateApiService
                .fetchCertificateKeywords()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((keywords) => {
                    this.certificateKeywords = keywords;
                });
        }
    }

    openCertificationDialog(certification?: Certification): void {
        if (this.selectedProvider.type === CertificationProviderTypeEnum.External) {
            this.openAddExternalCertificationDialog(certification);
        } else {
            this.openInternalCertificationDialog(certification);
        }
    }

    openMoveToDialog(certification: Certification): void {
        const dialogRef = this._matDialog.open(ProvidersListDialogComponent, {
            width: '930px',
            autoFocus: false,
        });

        dialogRef.componentInstance.certificationProvidersTotalCount$ = this.certificationProvidersTotalCount$;
        dialogRef.componentInstance.filteredCertificationProviders$ = this.filteredCertificationProviders$;
        dialogRef.componentInstance.providerToDisable = this.selectedProvider;

        dialogRef.afterClosed().subscribe((result: CertificationProvider) => {
            if (result) {
                this._store.dispatch(new MoveCertification({ certificationId: certification.id, providerId: result.id }));

                this._action$.pipe(ofActionDispatched(MoveCertificationSuccess), takeUntil(this._unsubscribeAll)).subscribe((action: MoveCertificationSuccess) => {
                    dialogRef.close();
                });
            }
        });
    }

    openAddExternalCertificationDialog(certification?: Certification): void {
        const dialogRef = this._matDialog.open(NameToggleDialogComponent, {
            width: '630px',
        });

        dialogRef.componentInstance.dialogTitle = certification ? 'PAGE.CERTIFICATIONS.BUTTON.EDIT' : 'PAGE.CERTIFICATIONS.BUTTON.ADD';
        dialogRef.componentInstance.placeholder = 'PAGE.CERTIFICATIONS.LABEL.NAME';
        dialogRef.componentInstance.buttonLabel = certification ? 'GENERAL.BUTTON.SAVE' : 'GENERAL.BUTTON.ADD';
        dialogRef.componentInstance.toggleLabel = 'GENERAL.LABEL.CERTIFICATE_EXPIRATION';
        dialogRef.componentInstance.disableUntilChanged = !!certification;
        dialogRef.componentInstance.name = certification?.certificationName;
        dialogRef.componentInstance.target = certification?.targetCount;
        dialogRef.componentInstance.toggle = certification?.hasExpirationDate;

        dialogRef.componentInstance.save.subscribe((result) => {
            if (result.name) {
                if (!certification) {
                    this._store.dispatch(
                        new CreateCertification({ name: result.name, providerId: this.selectedProvider.id, hasExpirationDate: result.toggleCtrl, targetCount: result.targetCount })
                    );

                    this._action$.pipe(ofActionDispatched(CreateCertificationSuccess), takeUntil(this._unsubscribeAll)).subscribe((action: CreateCertificationSuccess) => {
                        dialogRef.close();
                    });

                    this._action$.pipe(ofActionDispatched(CreateCertificationFailure), take(1)).subscribe((action: CreateCertificationFailure) => {
                        dialogRef.componentInstance.errors.next({ message: action.payload.response.error.errors.name });
                    });
                } else {
                    this._store.dispatch(
                        new UpdateCertification({ id: certification.id, name: result.name, hasExpirationDate: result.toggleCtrl, targetCount: result.targetCount })
                    );

                    this._action$.pipe(ofActionDispatched(UpdateCertificationSuccess), takeUntil(this._unsubscribeAll)).subscribe((action: UpdateCertificationSuccess) => {
                        dialogRef.close();
                    });

                    this._action$.pipe(ofActionDispatched(UpdateCertificationFailure), take(1)).subscribe((action: UpdateCertificationFailure) => {
                        dialogRef.componentInstance.errors.next({ message: action.payload.response.error.errors.name });
                    });
                }
            }
        });
    }

    openInternalCertificationDialog(certification?: Certification): void {
        const dialogRef = this._matDialog.open(InternalCertificationTemplateDialogComponent, {
            width: '630px',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = certification ? 'PAGE.CERTIFICATIONS.BUTTON.EDIT' : 'PAGE.CERTIFICATIONS.BUTTON.ADD';
        dialogRef.componentInstance.certificateKeywords = this.certificateKeywords;
        dialogRef.componentInstance.selectedProvider = this.selectedProvider;
        dialogRef.componentInstance.certification = certification;
        dialogRef.componentInstance.validity = certification ? certification.hasExpirationDate : true;
        dialogRef.componentInstance.saveBtnLoading$ = this.saveBtnLoading$;

        dialogRef.componentInstance.save.subscribe(
            async (result: { name: string; providerId: string; validityPeriod: number; hasExpirationDate: boolean; file: File; isFileUpdated: boolean; targetCount: number }) => {
                if (result) {
                    this.saveBtnLoading$.next(true);
                    const successMessage = this._translateService.instant('CERTIFICATE.MESSAGE.TEMPLATE_UPLOADED');

                    if (!certification) {
                        this._store.dispatch(
                            new CreateInternalCertification({
                                name: result.name,
                                providerId: result.providerId,
                                validityPeriod: result.hasExpirationDate ? result.validityPeriod : null,
                                file: result.file,
                                hasExpirationDate: result.hasExpirationDate,
                                targetCount: result.targetCount,
                            })
                        );

                        this._action$
                            .pipe(ofActionDispatched(CreateInternalCertificationSuccess), takeUntil(this._unsubscribeAll))
                            .subscribe((action: CreateInternalCertificationSuccess) => {
                                this.saveBtnLoading$.next(false);

                                dialogRef.close();

                                this._helperService.showSnackBarMessage(successMessage);

                                this.loadCertificationsPage();
                            });

                        this._action$.pipe(ofActionDispatched(CreateInternalCertificationFailure), take(1)).subscribe((action: CreateInternalCertificationFailure) => {
                            this.saveBtnLoading$.next(false);
                            dialogRef.componentInstance.errors.next({ message: action.payload.response.error.errors.name });
                        });
                    } else {
                        if (result.isFileUpdated && result.file) {
                            try {
                                await this._certificationApiService.updateTemplate(certification.id, result.file).then(() => {});
                            } catch (error) {
                                this.saveBtnLoading$.next(false);
                            }
                        }

                        this._store.dispatch(
                            new UpdateCertification({
                                id: certification.id,
                                name: result.name,
                                validityPeriod: result.hasExpirationDate ? result.validityPeriod : null,
                                hasExpirationDate: result.hasExpirationDate,
                                targetCount: result.targetCount,
                            })
                        );

                        this._action$.pipe(ofActionDispatched(UpdateCertificationSuccess), takeUntil(this._unsubscribeAll)).subscribe((action: UpdateCertificationSuccess) => {
                            this.saveBtnLoading$.next(false);

                            dialogRef.close();

                            this._helperService.showSnackBarMessage(successMessage);

                            this.loadCertificationsPage();
                        });

                        this._action$.pipe(ofActionDispatched(UpdateCertificationFailure), take(1)).subscribe((action: UpdateCertificationFailure) => {
                            this.saveBtnLoading$.next(false);

                            dialogRef.componentInstance.errors.next({ message: action.payload.response.error.errors.name });
                        });
                    }
                }
            }
        );
    }

    updateCertificationStatus(certification: Certification): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.actionButtonText = certification.status ? 'GENERAL.BUTTON.REACTIVATE' : 'GENERAL.BUTTON.ARCHIVE';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('PAGE.CERTIFICATIONS.MESSAGE.ARCHIVE', {
            action: certification.status ? this._translateService.instant('GENERAL.BUTTON.REACTIVATE') : this._translateService.instant('GENERAL.BUTTON.ARCHIVE'),
            name: certification.certificationName,
        });

        this.confirmDialogRef.afterClosed().subscribe(async (result: boolean) => {
            if (result) {
                await this._certificationService.updateCertificationStatus(
                    certification.id,
                    certification.status === CertificationProviderStatusEnum.Active ? CertificationProviderStatusEnum.Archived : CertificationProviderStatusEnum.Active
                );

                this.loadCertificationsPage();
            }

            this.confirmDialogRef = null;
        });
    }

    saveSelected(): void {
        this.dialogRef.close({});
    }

    async loadCertificationsPage(isFilterOrSearchActive = false): Promise<void> {
        this.isFilterOrSearchActive = isFilterOrSearchActive ? true : false;

        if (this.dataSource) {
            this.dataSource.loadCertifications(100, 1, this.searchInput.value, [this.selectedProvider.id], this.statusInput.value);
        }
    }
}
